/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, type FC } from 'react';
import type { TransportData } from '@/domainLogic/index.ts';
import { sumClasses, toYYYYMMDD } from '@/app/components/search/utils/misc';
import type { Dates } from '@/app/components/search/utils';
import { convertDate, getDaysRange } from '@/app/components/search/components/DatePickers/utils';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isUsLocale } from '@/app/common/measures/locale.ts';
import Day, { type DayProps } from './Day';
import { DaysStyled } from './styled';

export type DaysProps = {
  year: number;
  month: number;
  dates?: Dates;
  disableDatesOutsideBookingWindow: boolean;
  transportData: TransportData[] | undefined;
};

const disableDatesOutsideBookingWindowClasses = (transportData: TransportData[], dataDate: string) => {
  const isOvernight = transportData?.[0]?.overnight?.is_overnight;
  const lastSegment = transportData?.[0]?.outbound?.segments?.length || 1;
  const inboundDepartureDate = transportData?.[0]?.inbound?.segments?.[0]?.departure?.datetime;
  const outboundDepartureDate = transportData?.[0]?.outbound?.segments?.[0]?.departure?.datetime;
  const outboundArrivalDate = transportData?.[0]?.outbound?.segments?.[lastSegment - 1]?.arrival?.datetime;

  const isBeforeDepartureDate =
    convertDate(dataDate) < new Date(isOvernight ? outboundArrivalDate : outboundDepartureDate).setHours(0, 0, 0, 0);
  const isAfterReturnDate = `${dataDate}T02:00:00` > inboundDepartureDate;

  return [...(isBeforeDepartureDate ? ['day-disabled'] : []), ...(isAfterReturnDate ? ['day-disabled'] : [])];
};

const isBeforeToday = (dataDate: string) => convertDate(dataDate) < new Date().setHours(0, 0, 0, 0);

export const getDaysInMonth = ({
  month,
  year,
  dates,
  disableDatesOutsideBookingWindow = false,
  transportData,
}: {
  month: number;
  year: number;
  dates?: Dates;
  disableDatesOutsideBookingWindow: boolean;
  transportData?: TransportData[];
}): DayProps[] => {
  const startDate = dates?.departureDate.date;
  const endDate = dates?.returnDate.date;

  const processDay = (day: Date) => {
    const dataDate = toYYYYMMDD(day) as string;
    const hasDates = startDate && endDate;
    const daysRange = hasDates ? getDaysRange({ startDate, endDate }) : [];

    const className = sumClasses([
      ...(hasDates
        ? [
            ...(daysRange.indexOf(dataDate) !== -1 ? ['day-active'] : []),
            ...(daysRange[0] === dataDate ? ['day-active-start'] : []),
            ...(daysRange[daysRange.length - 1] === dataDate ? ['day-active-end'] : []),
          ]
        : [...(dataDate === startDate ? ['day-active-exact'] : [])]),
      ...(disableDatesOutsideBookingWindow && transportData
        ? [
            ...disableDatesOutsideBookingWindowClasses(transportData, dataDate),
            ...(isBeforeToday(dataDate) ? ['day-disabled'] : []),
          ]
        : [...(isBeforeToday(dataDate) ? ['day-disabled'] : [])]),
    ]);

    return { date: new Date(day), day: day.getDate(), dayOfWeek: day.getDay(), dataDate, className };
  };

  return Array.from({ length: new Date(year, month + 1, 0).getDate() }, (_, index) =>
    processDay(new Date(year, month, index + 1))
  );
};

export const daysToAdd = (count: number): string[] => {
  let i = 0;
  const days: any = [];
  do {
    days.push(i);
    i++;
  } while (i < count - 1);
  return days;
};

const Days: FC<DaysProps> = memo(
  ({ year, month, dates, disableDatesOutsideBookingWindow, transportData }) => {
    const { localization } = usePageWrapperData();
    const isUs = isUsLocale(localization.locale);
    const firstDayOfMonth = isUs ? new Date(year, month).getDay() + 1 : new Date(year, month).getDay();
    const days = getDaysInMonth({ month, year, dates, disableDatesOutsideBookingWindow, transportData });
    const lastDayOfMonth = new Date(year, month + 1, 0).getDay();

    return (
      <DaysStyled className="openx-ui-calendar-days">
        {firstDayOfMonth !== 1
          ? daysToAdd(firstDayOfMonth === 0 ? 7 : firstDayOfMonth).map((x) => <Day key={x} />)
          : null}
        {days.map((dayItem) => (
          <Day key={dayItem.dataDate} {...dayItem} />
        ))}
        {lastDayOfMonth !== 0 ? daysToAdd(8 - lastDayOfMonth).map((x) => <Day key={x} />) : null}
      </DaysStyled>
    );
  },
  (prevProps: DaysProps, nextProps: DaysProps) => {
    if (prevProps.year !== nextProps.year) return false;
    if (prevProps.month !== nextProps.month) return false;
    if (JSON.stringify(prevProps.dates) !== JSON.stringify(nextProps.dates)) return false;
    return true;
  }
);

Days.displayName = 'Days';

export default Days;
